import Dexie from 'dexie';
// AES and the UTF-8 encoder only: the full crypto-js bundle is about 230 K of source, and helpers/string (which held
// these two functions) is imported by most of the app. Same algorithm and output format, so values written before stay
// readable.
import AES from 'crypto-js/aes';
import Utf8 from 'crypto-js/enc-utf8';

export function encrypt(value, secret) {
  return AES.encrypt(value, secret).toString();
}

export function decrypt(value, secret) {
  return AES.decrypt(value, secret).toString(Utf8);
}

export function applyEncryptionMiddleware(database) {
  database.use({
    stack: 'dbcore',
    name: 'encryption',
    create(downLevelDatabase) {
      return {
        ...downLevelDatabase,
        table(tableName) {
          const downLevelTable = downLevelDatabase.table(tableName);

          return {
            ...downLevelTable,
            openCursor: openCursor.bind(this, downLevelTable),
            get: get.bind(this, downLevelTable),
            getMany: getMany.bind(this, downLevelTable),
            query: query.bind(this, downLevelTable),
            mutate: mutate.bind(this, downLevelTable),
          };
        },
      };
    },
  });
}

async function openCursor(table, req) {
  const cursor = await table.openCursor(req);
  if (!cursor) return cursor;

  return Object.create(cursor, {
    continue: {
      get() {
        return cursor.continue;
      },
    },
    continuePrimaryKey: {
      get() {
        return cursor.continuePrimaryKey;
      },
    },
    key: {
      get() {
        return cursor.key;
      },
    },
    value: {
      get() {
        return decryptValue(table, cursor.value);
      },
    },
  });
}

async function get(table, req) {
  const value = await table.get(req);
  return decryptValue(table, value);
}

async function getMany(table, req) {
  const values = await table.getMany(req);
  return values.map((value) => decryptValue(table, value));
}

async function query(table, req) {
  const response = await table.query(req);
  const result = await Dexie.Promise.all(response.result.map((value) => decryptValue(table, value)));
  return { ...response, result };
}

async function mutate(table, req) {
  if (req.type !== 'add' && req.type !== 'put') return table.mutate(req);
  const values = await Dexie.Promise.all(req.values.map((value) => encryptValue(table, value)));
  return table.mutate({ ...req, values });
}

function encryptValue(table, value) {
  const { primaryKey, indices } = getKeys(table);
  const toEncrypt = { encrypted: 'true' };
  const toStore = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const key in value) {
    if (key === primaryKey || indices.includes(key) || key === 'encrypted') {
      toStore[key] = value[key];
    } else {
      toEncrypt[key] = encrypt(JSON.stringify(value[key]), '123');
    }
  }

  return { ...toStore, ...toEncrypt };
}

function decryptValue(table, value) {
  if (value?.encrypted !== 'true') return value;

  const { primaryKey, indices } = getKeys(table);
  const toDecrypt = {};
  const toStore = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const key in value) {
    if (key === primaryKey || indices.includes(key) || key === 'encrypted') {
      toStore[key] = value[key];
    } else {
      try {
        toDecrypt[key] = JSON.parse(decrypt(value[key], '123'));
      } catch {
        toDecrypt[key] = value[key];
      }
    }
  }

  return { ...toStore, ...toDecrypt };
}

function getKeys(table) {
  const primaryKey = 'primKey' in table.schema ? table.schema.primKey.keyPath : table.schema.primaryKey.keyPath;
  const indices = table.schema.indexes.map((index) => index.keyPath);
  return { primaryKey, indices };
}
